import { useEffect, useState } from "react";
import { toast } from "sonner";
import { FilePdf, CalendarBlank, DownloadSimple } from "@phosphor-icons/react";
import { api, apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";

const REPORTS = [
  { k: "general",        l: "General SGSSO (todos los módulos)",         hint: "KPIs + semáforo por módulo + resumen de registros" },
  { k: "iper",           l: "IPER-C · Identificación de Peligros",       hint: "Matriz de riesgos con controles jerárquicos" },
  { k: "bowtie",         l: "Bow-Tie · Análisis de eventos",             hint: "Amenazas · consecuencias · barreras" },
  { k: "jsa",            l: "AST / JSA · Análisis de Seguridad",         hint: "Pasos · peligros · controles · firmas" },
  { k: "permits",        l: "Permisos de Alto Riesgo",                    hint: "Vigencia · checklist · aprobaciones" },
  { k: "observations",   l: "Observaciones de Seguridad",                 hint: "Actos/condiciones + fotos + acción inmediata" },
  { k: "toolbox",        l: "Charlas 5-Minutos",                          hint: "Actas + asistentes + firma" },
  { k: "investigations", l: "Investigaciones de Incidentes",              hint: "5-Por qué · ICAM · Ishikawa · plan de acción" },
  { k: "actions",        l: "Acciones Correctivas y Preventivas",         hint: "Matriz responsable / vencimiento / evidencia" },
];

const PRESETS = [
  { k: "today",     l: "Hoy" },
  { k: "week",      l: "Esta semana" },
  { k: "month",     l: "Este mes" },
  { k: "quarter",   l: "Este trimestre" },
  { k: "ytd",       l: "Año en curso" },
  { k: "all",       l: "Todo el histórico" },
];

function fmtDate(d) {
  return d.toISOString().slice(0, 10);
}

function computePreset(preset) {
  const now = new Date();
  const y = now.getFullYear();
  const m = now.getMonth();
  const d = now.getDate();
  if (preset === "today") return { from: fmtDate(now), to: fmtDate(now) };
  if (preset === "week") {
    const day = now.getDay() || 7;
    const monday = new Date(y, m, d - (day - 1));
    return { from: fmtDate(monday), to: fmtDate(now) };
  }
  if (preset === "month") return { from: fmtDate(new Date(y, m, 1)), to: fmtDate(now) };
  if (preset === "quarter") {
    const qStart = Math.floor(m / 3) * 3;
    return { from: fmtDate(new Date(y, qStart, 1)), to: fmtDate(now) };
  }
  if (preset === "ytd") return { from: fmtDate(new Date(y, 0, 1)), to: fmtDate(now) };
  return { from: "", to: "" };
}

export default function HseReportDialog({ open, onOpenChange }) {
  const [kind, setKind] = useState("general");
  const [preset, setPreset] = useState("month");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [downloading, setDownloading] = useState(false);

  useEffect(() => {
    if (open) {
      const p = computePreset("month");
      setFrom(p.from); setTo(p.to); setPreset("month"); setKind("general");
    }
  }, [open]);

  const applyPreset = (p) => {
    setPreset(p);
    const r = computePreset(p);
    setFrom(r.from); setTo(r.to);
  };

  const download = async () => {
    setDownloading(true);
    try {
      toast.info("Generando PDF…");
      const res = await api.get(`/hse/reports/${kind}`, {
        responseType: "blob",
        params: { from_date: from, to_date: to },
      });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      const suffix = (from || to) ? `${from}_${to}` : "all";
      a.download = `sgsso-${kind}-${suffix}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Reporte descargado");
      onOpenChange(false);
    } catch (e) { toast.error(apiError(e)); }
    finally { setDownloading(false); }
  };

  const currentReport = REPORTS.find((r) => r.k === kind);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl" data-testid="hse-report-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FilePdf size={18} weight="bold" className="text-red-700" />
            Descargar reporte SGSSO
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-5 py-2">
          {/* Report picker */}
          <div>
            <Label className="text-[10px] uppercase tracking-widest font-bold text-slate-500">
              Tipo de reporte
            </Label>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mt-2 max-h-[240px] overflow-y-auto pr-1">
              {REPORTS.map((r) => (
                <button
                  key={r.k}
                  onClick={() => setKind(r.k)}
                  data-testid={`hse-report-pick-${r.k}`}
                  className={`text-left p-2.5 rounded-md border transition ${
                    kind === r.k
                      ? "border-blue-900 bg-blue-50 ring-1 ring-blue-900"
                      : "border-slate-200 hover:border-slate-400 bg-white"
                  }`}
                >
                  <div className="text-sm font-semibold text-slate-900">{r.l}</div>
                  <div className="text-[11px] text-slate-500 mt-0.5">{r.hint}</div>
                </button>
              ))}
            </div>
          </div>

          {/* Preset chips */}
          <div>
            <Label className="text-[10px] uppercase tracking-widest font-bold text-slate-500">
              Período
            </Label>
            <div className="flex flex-wrap gap-1.5 mt-2">
              {PRESETS.map((p) => (
                <button
                  key={p.k}
                  onClick={() => applyPreset(p.k)}
                  data-testid={`hse-report-preset-${p.k}`}
                  className={`text-xs font-semibold px-3 py-1.5 rounded-full border transition ${
                    preset === p.k
                      ? "border-blue-900 bg-blue-900 text-white"
                      : "border-slate-200 bg-white text-slate-600 hover:border-slate-400"
                  }`}
                >
                  {p.l}
                </button>
              ))}
              <button
                onClick={() => setPreset("custom")}
                data-testid="hse-report-preset-custom"
                className={`text-xs font-semibold px-3 py-1.5 rounded-full border transition ${
                  preset === "custom"
                    ? "border-blue-900 bg-blue-900 text-white"
                    : "border-slate-200 bg-white text-slate-600 hover:border-slate-400"
                }`}
              >
                Personalizado
              </button>
            </div>
          </div>

          {/* Date range inputs */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label htmlFor="hse-report-from" className="text-xs text-slate-500 flex items-center gap-1">
                <CalendarBlank size={11} /> Desde
              </Label>
              <Input
                id="hse-report-from"
                type="date"
                value={from}
                onChange={(e) => { setFrom(e.target.value); setPreset("custom"); }}
                data-testid="hse-report-from"
                className="mt-1"
              />
            </div>
            <div>
              <Label htmlFor="hse-report-to" className="text-xs text-slate-500 flex items-center gap-1">
                <CalendarBlank size={11} /> Hasta
              </Label>
              <Input
                id="hse-report-to"
                type="date"
                value={to}
                onChange={(e) => { setTo(e.target.value); setPreset("custom"); }}
                data-testid="hse-report-to"
                className="mt-1"
              />
            </div>
          </div>

          {currentReport && (
            <div className="bg-slate-50 border border-slate-200 rounded-md p-3 text-xs text-slate-600">
              <b className="text-slate-900">{currentReport.l}</b><br />
              {currentReport.hint} · Nivel 3 auditable (portada + KPIs + detalle por registro + fotos)
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={downloading}>
            Cancelar
          </Button>
          <Button
            onClick={download}
            disabled={downloading}
            data-testid="hse-report-download"
            className="gap-2 bg-red-700 hover:bg-red-800"
          >
            <DownloadSimple size={14} weight="bold" />
            {downloading ? "Generando…" : "Descargar PDF"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
